export type ItemType = 'girl' | 'penis' | 'grenade' | 'monkey' | 'dog';

export class Item {
  public x: number;
  public y: number;
  public prevY: number;
  public vy: number;
  public radius: number = 26;
  public rotation: number = 0;
  public rotationSpeed: number;
  public type: ItemType;
  public isCaught: boolean = false;
  public customImage: HTMLImageElement | null = null;
  public girlVariant: number = 0;
  public flipX: boolean = false;
  private swayTimer: number;

  constructor(x: number, y: number, vy: number, type: ItemType, customImage: HTMLImageElement | null = null) {
    this.x = x;
    this.y = y;
    this.prevY = y;
    this.vy = vy;
    this.type = type;
    this.customImage = customImage;
    this.girlVariant = Math.floor(Math.random() * 3);
    this.flipX = Math.random() > 0.5;
    this.swayTimer = Math.random() * Math.PI * 2;
    this.rotation = (Math.random() - 0.5) * 0.4;
    this.rotationSpeed = (Math.random() - 0.5) * 2.2;
  }

  public get bottomOffset(): number {
    // Pin-up toes reach ~47px below center when scaled to height 94px
    return this.type === 'girl' ? 47 : this.radius * 0.75;
  }

  public update(dt: number): void {
    this.prevY = this.y;
    this.y += this.vy * dt;

    if (this.type === 'girl') {
      // Elegant wind sway keeping her head up and legs down towards the tray
      this.swayTimer += dt * 3.2;
      this.rotation = Math.sin(this.swayTimer) * 0.08;
    } else {
      this.rotation += this.rotationSpeed * dt;
    }
  }

  public draw(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.translate(this.x, this.y);

    // Subtle dark circular contrast aura behind round items (skip for girl to keep her natural pinup silhouette)
    if (this.type !== 'girl') {
      ctx.fillStyle = 'rgba(10, 0, 20, 0.5)';
      ctx.beginPath();
      ctx.arc(0, 0, this.radius + 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.rotate(this.rotation);

    if (this.flipX && this.type === 'girl') {
      ctx.scale(-1, 1);
    }

    if (this.customImage && this.customImage.complete && this.customImage.naturalWidth > 0) {
      if (this.type === 'girl') {
        const h = 94;
        const aspect = this.customImage.naturalWidth / (this.customImage.naturalHeight || 1);
        const w = Math.round(h * (aspect > 0 ? aspect : 0.412));

        // Soft neon contour aura matching VIP club lighting
        ctx.save();
        ctx.shadowColor = '#ff007f';
        ctx.shadowBlur = 14;
        ctx.drawImage(this.customImage, -w / 2, -h / 2, w, h);
        ctx.restore();

        ctx.drawImage(this.customImage, -w / 2, -h / 2, w, h);
      } else {
        const size = this.radius * 2;
        ctx.drawImage(this.customImage, -this.radius, -this.radius, size, size);
      }
      ctx.restore();
      return;
    }

    // Procedural Fallback Graphics for each item type
    switch (this.type) {
      case 'girl':
        this.drawGirl(ctx);
        break;
      case 'penis':
        this.drawPenis(ctx);
        break;
      case 'grenade':
        this.drawGrenade(ctx);
        break;
      case 'monkey':
        this.drawMonkey(ctx);
        break;
      case 'dog':
        this.drawDog(ctx);
        break;
    }

    ctx.restore();
  }

  // --- Optimized Procedural Vector Renderers ---

  private drawGirl(ctx: CanvasRenderingContext2D): void {
    // Seductive Hourglass Pin-up Model
    // 3 Unique VIP Variants:
    // 0: Platinum Blonde + Neon Pink Bikini
    // 1: Brunette + Cherry Red Lace
    // 2: Redhead + Cyan Luxury / Gold Trim
    const palettes = [
      {
        skin: '#fedec0',
        skinShadow: '#d89768',
        skinHighlight: '#fff2e3',
        hair: '#ffdb4d',
        hairHighlight: '#fffbe6',
        hairShadow: '#c29213',
        bikini: '#ff007f',
        bikiniHighlight: '#ff66b2',
        bikiniTrim: '#ffd700',
        shoe: '#ff0055',
        lips: '#ff0055',
        glow: '#ff007f'
      },
      {
        skin: '#fbd0ad',
        skinShadow: '#ce8857',
        skinHighlight: '#ffece0',
        hair: '#361b0f',
        hairHighlight: '#683722',
        hairShadow: '#190a04',
        bikini: '#e60026',
        bikiniHighlight: '#ff4d66',
        bikiniTrim: '#ffffff',
        shoe: '#181820',
        lips: '#d40022',
        glow: '#ff1a40'
      },
      {
        skin: '#ffe1cf',
        skinShadow: '#d9946e',
        skinHighlight: '#fff5ed',
        hair: '#d63d00',
        hairHighlight: '#ff7733',
        hairShadow: '#8a2100',
        bikini: '#00e5ff',
        bikiniHighlight: '#80f2ff',
        bikiniTrim: '#ffd700',
        shoe: '#00b4cc',
        lips: '#ff1493',
        glow: '#00e5ff'
      }
    ];
    const p = palettes[this.girlVariant % palettes.length];

    ctx.save();
    const scale = 0.88;
    ctx.scale(scale, scale);

    // Soft natural radial contrast aura behind body for readability on any background
    ctx.save();
    const aura = ctx.createRadialGradient(0, 10, 6, 0, 10, 44);
    aura.addColorStop(0, 'rgba(10, 0, 22, 0.7)');
    aura.addColorStop(0.7, 'rgba(10, 0, 22, 0.25)');
    aura.addColorStop(1, 'transparent');
    ctx.fillStyle = aura;
    ctx.beginPath();
    ctx.ellipse(0, 12, 30, 52, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // 1. Flowing Back Hair (trailing in the wind as she falls)
    ctx.fillStyle = p.hairShadow;
    ctx.beginPath();
    ctx.moveTo(-6, -28);
    ctx.bezierCurveTo(-18, -25, -26, -10, -22, 6);
    ctx.bezierCurveTo(-20, 16, -14, 20, -10, 12);
    ctx.bezierCurveTo(-14, 2, -12, -15, -4, -24);
    ctx.closePath();
    ctx.fill();

    // 2. Extended Back Arm (reaching gracefully down)
    ctx.strokeStyle = p.skinShadow;
    ctx.lineWidth = 3.2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(-3, -16);
    ctx.bezierCurveTo(-10, -8, -14, 0, -12, 10);
    ctx.stroke();

    // 3. Back Leg & High Heel (bent gracefully behind in depth layer)
    ctx.fillStyle = p.skinShadow;
    ctx.beginPath();
    ctx.moveTo(-6, 8);
    ctx.bezierCurveTo(-14, 14, -18, 26, -12, 38);
    ctx.bezierCurveTo(-9, 44, -12, 50, -15, 56);
    ctx.lineTo(-11, 58);
    ctx.bezierCurveTo(-8, 52, -5, 42, -2, 32);
    ctx.bezierCurveTo(0, 24, -2, 14, -4, 8);
    ctx.closePath();
    ctx.fill();

    // Back Stiletto Shoe
    ctx.fillStyle = p.shoe;
    ctx.beginPath();
    ctx.moveTo(-15, 56);
    ctx.lineTo(-10, 60);
    ctx.lineTo(-13, 64);
    ctx.lineTo(-17, 59);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = p.shoe;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(-15, 59);
    ctx.lineTo(-17, 66);
    ctx.stroke();

    // 4. Front Leg (shapely, smooth, pointed toe)
    ctx.fillStyle = p.skin;
    ctx.beginPath();
    ctx.moveTo(4, 10);
    ctx.bezierCurveTo(9, 18, 11, 30, 8, 42);
    ctx.bezierCurveTo(6, 48, 5, 54, 3, 60);
    ctx.lineTo(-1, 60);
    ctx.bezierCurveTo(0, 52, 2, 44, 1, 36);
    ctx.bezierCurveTo(0, 28, -2, 20, -4, 12);
    ctx.closePath();
    ctx.fill();

    // Front Stiletto Shoe (pointed down towards tray)
    ctx.fillStyle = p.shoe;
    ctx.beginPath();
    ctx.moveTo(3, 59);
    ctx.lineTo(6, 66);
    ctx.lineTo(1, 68);
    ctx.lineTo(-1, 61);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = p.shoe;
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.moveTo(0, 61);
    ctx.lineTo(-3, 69);
    ctx.stroke();

    // Heel Specular Highlight
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(3, 62);
    ctx.lineTo(5, 66);
    ctx.stroke();

    // 5. Main Torso: Slender Waist & Hourglass Arch
    ctx.fillStyle = p.skin;
    ctx.beginPath();
    ctx.moveTo(-2, -20);
    ctx.bezierCurveTo(-7, -12, -7, -4, -3, 2);
    ctx.bezierCurveTo(-4, 4, -8, 6, -12, 8);
    ctx.bezierCurveTo(-16, 14, -10, 20, -4, 14);
    ctx.bezierCurveTo(0, 14, 3, 12, 4, 10);
    ctx.bezierCurveTo(2, 4, 2, 0, 4, -4);
    ctx.bezierCurveTo(6, -8, 8, -14, 5, -20);
    ctx.closePath();
    ctx.fill();

    // Belly button
    ctx.fillStyle = p.skinShadow;
    ctx.beginPath();
    ctx.arc(1.5, 3, 1.2, 0, Math.PI * 2);
    ctx.fill();

    // 6. Voluptuous Buttocks ("Жопа") - 3D Rounded Cheeks
    const buttGradient = ctx.createRadialGradient(-10, 9, 2, -10, 9, 13);
    buttGradient.addColorStop(0, p.skinHighlight);
    buttGradient.addColorStop(0.55, p.skin);
    buttGradient.addColorStop(1, p.skinShadow);

    ctx.fillStyle = buttGradient;
    ctx.beginPath();
    ctx.ellipse(-10, 9, 10, 9.5, -0.2, 0, Math.PI * 2);
    ctx.fill();

    // Gluteal crease line
    ctx.strokeStyle = p.skinShadow;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.arc(-7, 9, 8, 0.3 * Math.PI, 0.85 * Math.PI);
    ctx.stroke();

    // 7. Micro Thong / Tanga Bikini Bottom
    ctx.fillStyle = p.bikini;
    ctx.beginPath();
    ctx.moveTo(-16, 8);
    ctx.lineTo(-8, 7);
    ctx.bezierCurveTo(-9, 12, -12, 14, -14, 12);
    ctx.closePath();
    ctx.fill();

    // Thong Hip String
    ctx.strokeStyle = p.bikiniTrim;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(-16, 8);
    ctx.quadraticCurveTo(-6, 5, 3, 7);
    ctx.stroke();

    // 8. Voluptuous Breasts & Cleavage ("Сиськи")
    ctx.fillStyle = p.skin;
    ctx.beginPath();
    ctx.arc(8, -12, 7.5, 0, Math.PI * 2);
    ctx.fill();

    const breastGrad = ctx.createRadialGradient(10, -9, 1, 10, -9, 8);
    breastGrad.addColorStop(0, p.skinHighlight);
    breastGrad.addColorStop(0.5, p.skin);
    breastGrad.addColorStop(1, p.skinShadow);

    ctx.fillStyle = breastGrad;
    ctx.beginPath();
    ctx.arc(10, -8, 8, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = p.skinShadow;
    ctx.lineWidth = 1.3;
    ctx.beginPath();
    ctx.moveTo(4, -14);
    ctx.quadraticCurveTo(6, -8, 7, -3);
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(10, -8, 8, 0.25 * Math.PI, 0.75 * Math.PI);
    ctx.stroke();

    // 9. Seductive Bikini Top / Bra Cups
    ctx.fillStyle = p.bikini;
    ctx.beginPath();
    ctx.arc(5, -12, 5.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.beginPath();
    ctx.arc(11, -8, 6.2, 0, Math.PI * 2);
    ctx.fill();

    // Glossy Cup Sheen
    ctx.fillStyle = p.bikiniHighlight;
    ctx.beginPath();
    ctx.ellipse(12, -10, 3, 1.5, -0.4, 0, Math.PI * 2);
    ctx.fill();

    // Halter Straps
    ctx.strokeStyle = p.bikiniTrim;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(8, -14);
    ctx.lineTo(1, -22);
    ctx.moveTo(5, -9);
    ctx.lineTo(-4, -8);
    ctx.stroke();

    // 10. Front Arm Gracefully Touching Hair
    ctx.fillStyle = p.skin;
    ctx.strokeStyle = p.skin;
    ctx.lineWidth = 3.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(2, -18);
    ctx.quadraticCurveTo(-4, -28, 2, -34);
    ctx.stroke();

    // Gold Bracelet
    ctx.strokeStyle = p.bikiniTrim;
    ctx.lineWidth = 2.2;
    ctx.beginPath();
    ctx.arc(-1, -30, 2, 0, Math.PI * 2);
    ctx.stroke();

    // 11. Head, Face, Eyelashes & Glossy Lips
    ctx.fillStyle = p.skin;
    ctx.beginPath();
    ctx.moveTo(-1, -19);
    ctx.lineTo(4, -19);
    ctx.lineTo(3, -25);
    ctx.lineTo(-1, -25);
    ctx.closePath();
    ctx.fill();

    ctx.beginPath();
    ctx.ellipse(1.5, -28, 6.5, 7.5, 0.25, 0, Math.PI * 2);
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(6, -30);
    ctx.lineTo(8, -27);
    ctx.lineTo(6, -26);
    ctx.lineTo(6, -23);
    ctx.lineTo(2, -22);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = 'rgba(255, 0, 100, 0.4)';
    ctx.beginPath();
    ctx.arc(4, -27, 2.6, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#181014';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.arc(5, -29, 2.2, 0.8 * Math.PI, 1.8 * Math.PI);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(7, -30);
    ctx.lineTo(9, -32);
    ctx.stroke();

    ctx.fillStyle = p.lips;
    ctx.beginPath();
    ctx.ellipse(7.5, -25, 2.2, 1.3, 0.1, 0, Math.PI * 2);
    ctx.fill();

    // 12. Luxurious Flowing Front Locks
    ctx.fillStyle = p.hair;
    ctx.beginPath();
    ctx.moveTo(-3, -34);
    ctx.bezierCurveTo(5, -37, 10, -32, 7, -26);
    ctx.bezierCurveTo(4, -22, 1, -22, -1, -27);
    ctx.bezierCurveTo(-8, -28, -12, -20, -10, -10);
    ctx.bezierCurveTo(-15, -12, -16, -24, -3, -34);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = p.hairHighlight;
    ctx.beginPath();
    ctx.ellipse(3, -34, 4.5, 1.8, -0.3, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private drawPenis(ctx: CanvasRenderingContext2D): void {
    const r = this.radius;

    // Outer glow
    ctx.shadowColor = '#00f0ff';
    ctx.shadowBlur = 10;
    ctx.strokeStyle = '#00f0ff';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Reset shadow blur
    ctx.shadowBlur = 0;

    // Shaft
    ctx.fillStyle = '#ffb088';
    ctx.strokeStyle = '#d97b53';
    ctx.lineWidth = 2;

    ctx.beginPath();
    ctx.roundRect(-r * 0.28, -r * 0.8, r * 0.56, r * 1.15, [14, 14, 6, 6]);
    ctx.fill();
    ctx.stroke();

    // Glans head
    ctx.fillStyle = '#ff7b90';
    ctx.beginPath();
    ctx.arc(0, -r * 0.72, r * 0.35, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Slit
    ctx.strokeStyle = '#c4455b';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, -r * 0.95);
    ctx.lineTo(0, -r * 0.65);
    ctx.stroke();

    // Testicles
    ctx.fillStyle = '#ffb088';
    ctx.strokeStyle = '#d97b53';
    ctx.beginPath();
    ctx.arc(-r * 0.38, r * 0.42, r * 0.36, 0, Math.PI * 2);
    ctx.arc(r * 0.38, r * 0.42, r * 0.36, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Warning badge
    ctx.font = '15px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('⚡', 0, 0);
  }

  private drawGrenade(ctx: CanvasRenderingContext2D): void {
    const r = this.radius;

    // Glow aura
    ctx.shadowColor = '#ff3300';
    ctx.shadowBlur = 12;
    ctx.strokeStyle = '#ff3300';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Reset shadow blur
    ctx.shadowBlur = 0;

    // Grenade body (military dark olive green)
    ctx.fillStyle = '#3f5936';
    ctx.strokeStyle = '#22331c';
    ctx.lineWidth = 2;

    ctx.beginPath();
    ctx.ellipse(0, r * 0.1, r * 0.65, r * 0.75, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Grid fragmentation grooves
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.moveTo(-r * 0.5, r * 0.1);
    ctx.lineTo(r * 0.5, r * 0.1);
    ctx.moveTo(-r * 0.4, -r * 0.2);
    ctx.lineTo(r * 0.4, -r * 0.2);
    ctx.moveTo(-r * 0.4, r * 0.4);
    ctx.lineTo(r * 0.4, r * 0.4);
    ctx.moveTo(0, -r * 0.6);
    ctx.lineTo(0, r * 0.75);
    ctx.stroke();

    // Metal collar & fuse cap
    ctx.fillStyle = '#9e9e9e';
    ctx.fillRect(-r * 0.22, -r * 0.85, r * 0.44, r * 0.3);

    // Pull ring
    ctx.strokeStyle = '#ffcc00';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(-r * 0.38, -r * 0.75, r * 0.22, 0, Math.PI * 2);
    ctx.stroke();

    // Sparking fuse tip
    ctx.fillStyle = '#ffdd00';
    ctx.beginPath();
    ctx.arc(r * 0.2, -r * 0.9, 4, 0, Math.PI * 2);
    ctx.fill();
  }

  private drawMonkey(ctx: CanvasRenderingContext2D): void {
    const r = this.radius;

    // Reset shadow blur
    ctx.shadowBlur = 0;

    // Ears
    ctx.fillStyle = '#8d5524';
    ctx.beginPath();
    ctx.arc(-r * 0.65, -r * 0.1, r * 0.3, 0, Math.PI * 2);
    ctx.arc(r * 0.65, -r * 0.1, r * 0.3, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#ffdbac';
    ctx.beginPath();
    ctx.arc(-r * 0.65, -r * 0.1, r * 0.16, 0, Math.PI * 2);
    ctx.arc(r * 0.65, -r * 0.1, r * 0.16, 0, Math.PI * 2);
    ctx.fill();

    // Head
    ctx.fillStyle = '#8d5524';
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.72, 0, Math.PI * 2);
    ctx.fill();

    // Face mask
    ctx.fillStyle = '#ffdbac';
    ctx.beginPath();
    ctx.ellipse(0, r * 0.12, r * 0.52, r * 0.42, 0, 0, Math.PI * 2);
    ctx.fill();

    // Eyes
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(-r * 0.25, -r * 0.15, r * 0.16, 0, Math.PI * 2);
    ctx.arc(r * 0.25, -r * 0.15, r * 0.16, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.arc(-r * 0.22, -r * 0.15, r * 0.09, 0, Math.PI * 2);
    ctx.arc(r * 0.28, -r * 0.15, r * 0.09, 0, Math.PI * 2);
    ctx.fill();

    // Smile
    ctx.strokeStyle = '#613915';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, r * 0.16, r * 0.2, 0.2, Math.PI - 0.2);
    ctx.stroke();

    // Banana badge
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('🍌', 0, r * 0.65);
  }

  private drawDog(ctx: CanvasRenderingContext2D): void {
    const r = this.radius;

    // Reset shadow blur
    ctx.shadowBlur = 0;

    // Floppy ears
    ctx.fillStyle = '#9b6b43';
    ctx.beginPath();
    ctx.ellipse(-r * 0.6, -r * 0.05, r * 0.22, r * 0.45, -0.3, 0, Math.PI * 2);
    ctx.ellipse(r * 0.6, -r * 0.05, r * 0.22, r * 0.45, 0.3, 0, Math.PI * 2);
    ctx.fill();

    // Head
    ctx.fillStyle = '#c79c6e';
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.68, 0, Math.PI * 2);
    ctx.fill();

    // White snout
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.ellipse(0, r * 0.18, r * 0.36, r * 0.32, 0, 0, Math.PI * 2);
    ctx.fill();

    // Nose
    ctx.fillStyle = '#222222';
    ctx.beginPath();
    ctx.arc(0, r * 0.06, r * 0.13, 0, Math.PI * 2);
    ctx.fill();

    // Eyes
    ctx.fillStyle = '#111111';
    ctx.beginPath();
    ctx.arc(-r * 0.24, -r * 0.18, r * 0.1, 0, Math.PI * 2);
    ctx.arc(r * 0.24, -r * 0.18, r * 0.1, 0, Math.PI * 2);
    ctx.fill();

    // Tongue
    ctx.fillStyle = '#ff6b8b';
    ctx.beginPath();
    ctx.arc(0, r * 0.35, r * 0.14, 0, Math.PI);
    ctx.fill();
  }
}
